import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { Users, Bot, CornerDownRight, ChevronDown, ChevronRight, UserPlus } from "lucide-react";

const STORE_KEY = "atlas.orgchart.collapsed";

// Read-only org chart v1. Built from each teammate's reports_to (humans) and each hired
// agent's manager_id (surfaced as reports_to). Vacancies (Team Sheet open positions) are
// anchored under the team lead (by email) when resolvable; otherwise shown in a strip below.
function buildForest(teammates, vacancies) {
  const all = [...teammates, ...(vacancies || [])];
  const byId = {};
  all.forEach((t) => { byId[t.id] = { ...t, children: [] }; });
  const roots = [];
  all.forEach((t) => {
    const parent = t.reports_to ? byId[t.reports_to] : null;
    if (parent && parent.id !== t.id) parent.children.push(byId[t.id]);
    else if (t.kind !== "vacancy") roots.push(byId[t.id]);
  });
  const rank = (n) => (n.kind === "person" ? 0 : n.kind === "agent" ? 1 : 2);
  const cmp = (a, b) => rank(a) - rank(b) || (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());
  const sortRec = (nodes) => { nodes.sort(cmp); nodes.forEach((n) => sortRec(n.children)); };
  sortRec(roots);
  return roots;
}

function renderNode(node, depth, collapsed, toggle) {
  const hasKids = node.children.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const indent = depth > 0 ? <CornerDownRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" /> : null;

  if (node.kind === "vacancy") {
    return (
      <div key={node.id} data-testid={`org-node-${node.id}`}>
        <div className="flex items-center gap-2 py-1.5">
          {indent}
          <span className="w-5 h-5 flex-shrink-0" />
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-amber-400 bg-amber-50 px-3 py-1.5" data-testid={`org-vacancy-${node.id}`}>
            <UserPlus className="w-4 h-4 text-amber-500" />
            <span className="font-medium text-amber-900 text-sm">{node.name}</span>
            <span className="chip border-amber-300 text-amber-700 bg-white">Open position</span>
            {node.team_name && <span className="text-xs text-amber-700 hidden sm:inline">{node.team_name}</span>}
          </div>
        </div>
      </div>
    );
  }

  const isAgent = node.kind === "agent";
  return (
    <div key={node.id} data-testid={`org-node-${node.id}`}>
      <div className="flex items-center gap-2 py-1.5">
        {indent}
        {hasKids ? (
          <button type="button" data-testid={`org-toggle-${node.id}`} onClick={() => toggle(node.id)}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500 flex-shrink-0"
            aria-label={isCollapsed ? "Expand" : "Collapse"}>
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        ) : <span className="w-5 h-5 flex-shrink-0" />}
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 ${isAgent ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-white"}`}>
          {isAgent ? <Bot className="w-4 h-4 text-indigo-500" /> : <Users className="w-4 h-4 text-slate-400" />}
          <span className="font-medium text-slate-900 text-sm">{node.name || "Unnamed"}</span>
          {isAgent && <span className="chip border-indigo-200 text-indigo-700 bg-indigo-50" data-testid={`org-agent-badge-${node.id}`}>AI agent</span>}
          {node.is_provider && <span className="chip border-orange-200 text-orange-700 bg-orange-50" data-testid={`org-provider-badge-${node.id}`}>Total Operations</span>}
          {(node.roles || []).length > 0 && !isAgent && <span className="text-xs text-slate-400 hidden sm:inline">{node.roles.join(", ")}</span>}
          {hasKids && isCollapsed && (
            <span className="chip border-slate-200 text-slate-500 bg-slate-50" data-testid={`org-hidden-count-${node.id}`}>
              {node.children.length} {node.children.length === 1 ? "report" : "reports"} hidden
            </span>
          )}
        </div>
      </div>
      {hasKids && !isCollapsed && (
        <div className="ml-4 border-l border-slate-100 pl-3">
          {node.children.map((c) => renderNode(c, depth + 1, collapsed, toggle))}
        </div>
      )}
    </div>
  );
}

export default function OrgChart() {
  const [roots, setRoots] = useState([]);
  const [unanchored, setUnanchored] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem(STORE_KEY) || "[]")); } catch { return new Set(); }
  });

  const toggle = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      try { sessionStorage.setItem(STORE_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  useEffect(() => {
    api.get("/directory").then((r) => {
      const t = r.data.teammates || [];
      const v = r.data.vacancies || [];
      setCount(t.length + v.length + (r.data.unanchored_openings || []).length);
      setRoots(buildForest(t, v));
      setUnanchored(r.data.unanchored_openings || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="org-chart-page">
      <PageHeader title="Org chart" subtitle="Reporting lines across this company. People and AI agents on one chart. Edit reporting lines in the Directory." />
      {!loading && count === 0 ? (
        <EmptyState icon={Users} title="No one to chart yet" description="Invite people or hire agents and set their reporting lines in the Directory." />
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="org-chart-tree">
            {roots.map((n) => renderNode(n, 0, collapsed, toggle))}
          </div>
          {unanchored.length > 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="unanchored-openings">
              <div className="text-sm font-semibold text-amber-800">Unanchored openings ({unanchored.length})</div>
              <div className="text-xs text-amber-700 mt-1 mb-3">Open positions with no resolvable manager. They still count here. Set a team lead by email in Team Sheet to place them on the chart.</div>
              <div className="flex flex-wrap gap-2">
                {unanchored.map((o) => (
                  <span key={o.id} data-testid={`unanchored-${o.id}`} className="chip border-amber-300 text-amber-800 bg-white">
                    {o.title}{o.team_name ? ` \u00b7 ${o.team_name}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
